import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  Mail, 
  Handshake, 
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nonprofit-staging-1/artifacts/7cdd5sle_cropped-HPC-LOGO.jpeg";

const navItems = [
  { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/admin/blog', icon: FileText, label: 'Blog Posts' },
  { href: '/admin/comments', icon: MessageSquare, label: 'Comments' },
  { href: '/admin/contacts', icon: Mail, label: 'Contacts' },
  { href: '/admin/partnerships', icon: Handshake, label: 'Partnerships' },
];

export const AdminLayout = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#2D3E55] z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full" />
          <span className="text-white font-heading font-semibold">Admin</span>
        </div>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-2"
          data-testid="mobile-sidebar-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-[#2D3E55] to-[#1a2634] z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 lg:h-20 flex items-center gap-3 px-6 border-b border-white/10">
          <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full" />
          <span className="text-white font-heading font-semibold">Admin Panel</span>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/admin' && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm transition-all ${
                  isActive 
                    ? 'bg-[#F15A24] text-white' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            Sign Out
          </Button>
          <Link to="/" className="block mt-2">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/10"
            >
              View Website
            </Button>
          </Link>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
